import { apiJson } from './api';
import type { DofPublication, Law } from '@/types';

export interface ArticleSearchResult {
  id: string;
  lawId: string;
  number: string;
  articleNumber?: string;
  title?: string;
  articleTitle?: string;
  content?: string;
  law?: { id: string; title: string };
  [key: string]: unknown;
}

export interface SemanticArticleSearchResult extends ArticleSearchResult {
  similarityScore?: number;
}

export async function searchLaws(query: string): Promise<Law[]> {
  if (!query.trim()) return [];
  const q = new URLSearchParams({ query: query.trim(), type: 'law' });
  return apiJson<Law[]>(`/api/laws/search?${q}`);
}

export async function searchArticles(query: string, lawIds?: string[]): Promise<ArticleSearchResult[]> {
  if (!query.trim()) return [];
  const params = new URLSearchParams({ search: query.trim() });
  if (lawIds?.length) params.set('laws', lawIds.join(','));
  const data = await apiJson<ArticleSearchResult[] | unknown>(`/api/articles?${params}`);
  return Array.isArray(data) ? data : [];
}

export async function searchDofLiteral(query: string): Promise<DofPublication[]> {
  if (!query.trim()) return [];
  const params = new URLSearchParams({
    q: query.trim(),
    page: '0',
    size: '10',
  });
  const data = await apiJson<{ items?: DofPublication[] } | unknown>(`/api/dof?${params}`);
  return typeof data === 'object' && data !== null && Array.isArray((data as { items?: DofPublication[] }).items)
    ? (data as { items: DofPublication[] }).items
    : [];
}

export async function searchArticlesSemantic(query: string, lawIds?: string[]): Promise<SemanticArticleSearchResult[]> {
  if (!query.trim()) return [];
  const data = await apiJson<{ results?: SemanticArticleSearchResult[] } | unknown>('/api/lex/semantic-search', {
    method: 'POST',
    body: JSON.stringify({
      query: query.trim(),
      lawIds: lawIds?.length ? lawIds : undefined,
      limit: 12,
      minScore: 0.3,
    }),
  });
  return typeof data === 'object' && data !== null && Array.isArray((data as { results?: SemanticArticleSearchResult[] }).results)
    ? (data as { results: SemanticArticleSearchResult[] }).results
    : [];
}

export async function searchDofSemantic(query: string): Promise<DofPublication[]> {
  if (!query.trim()) return [];
  const data = await apiJson<{ results?: DofPublication[] } | unknown>('/api/dof/search', {
    method: 'POST',
    body: JSON.stringify({
      query: query.trim(),
      limit: 10,
      minScore: 0.3,
    }),
  });
  return typeof data === 'object' && data !== null && Array.isArray((data as { results?: DofPublication[] }).results)
    ? (data as { results: DofPublication[] }).results
    : [];
}

export async function searchAll(
  query: string,
  lawIds?: string[]
): Promise<{ laws: Law[]; articles: ArticleSearchResult[]; dof: DofPublication[] }> {
  const [laws, articles, dof] = await Promise.all([
    searchLaws(query),
    searchArticles(query, lawIds),
    searchDofLiteral(query),
  ]);
  return { laws, articles, dof };
}
